import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { Folder } from '../types/models'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface FoldersResponse {
  folders: Folder[]
}
export function useFolders() {
  const { user, firebaseUser } = useAuth()

  return useQuery({
    queryKey: ['folders', user?.orgId],
    queryFn: async () => {
      if (!firebaseUser || !user?.orgId) throw new Error('Not authenticated or orgId is missing')

      const token = await firebaseUser.getIdToken()
      
      // トークンの詳細をデバッグ
      const tokenResult = await firebaseUser.getIdTokenResult()
      console.log('📁 Folders API token details:', {
        claims: tokenResult.claims,
        orgId: tokenResult.claims.orgId,
        role: tokenResult.claims.role,
        userOrgId: user.orgId,
        apiUrl: `${API_BASE}/v1/organizations/${user.orgId}/folders`
      })
      
      const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/folders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorText = await response.text().catch(() => '')
        let errorData: any = {}
        try {
          errorData = JSON.parse(errorText)
        } catch {
          errorData = { error: errorText || `HTTP ${response.status}` }
        }
        console.error('❌ Folders API error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData.error || errorText
        })
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return response.json()
    },
    enabled: !!firebaseUser && !!user?.orgId,
    staleTime: 5 * 60 * 1000, // 5分間キャッシュを有効にする（ナビゲーション時はキャッシュを即表示）
    refetchOnWindowFocus: false,
  })
}

export function useCreateFolder() {
  const { user, firebaseUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      name: string
      description?: string
      color?: string
    }) => {
      if (!firebaseUser || !user?.orgId) throw new Error('Not authenticated or orgId is missing')

      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/folders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return response.json()
    },
    onSuccess: async () => {
      // キャッシュを完全にクリアして再取得
      queryClient.removeQueries({ queryKey: ['folders', user?.orgId] })
      await queryClient.invalidateQueries({ queryKey: ['folders', user?.orgId] })
      // 強制的に再取得
      await queryClient.refetchQueries({ queryKey: ['folders', user?.orgId] })
    }
  })
}

export function useUpdateFolder() {
  const { user, firebaseUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      id: string
      name?: string
      description?: string
      color?: string
    }) => {
      if (!firebaseUser || !user?.orgId) throw new Error('Not authenticated or orgId is missing')

      const { id, ...updateData } = data
      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/folders/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return response.json()
    },
    onSuccess: async (_, variables) => {
      // キャッシュを完全にクリアして再取得
      queryClient.removeQueries({ queryKey: ['folders', user?.orgId] })
      queryClient.removeQueries({ queryKey: ['folder', variables.id] })
      await queryClient.invalidateQueries({ queryKey: ['folders', user?.orgId] })
      await queryClient.invalidateQueries({ queryKey: ['folder', variables.id] })
      // 強制的に再取得
      await queryClient.refetchQueries({ queryKey: ['folders', user?.orgId] })
    }
  })
}

export function useSetFolderArchived() {
  const { user, firebaseUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { id: string; archived: boolean }) => {
      if (!firebaseUser || !user?.orgId) throw new Error('Not authenticated or orgId is missing')
      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/folders/${data.id}/archive`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ archived: data.archived })
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }
      return response.json()
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['folders', user?.orgId] })
      await queryClient.refetchQueries({ queryKey: ['folders', user?.orgId] })
    }
  })
}

export function useDeleteFolder() {
  const { user, firebaseUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (folderId: string) => {
      if (!firebaseUser || !user?.orgId) throw new Error('Not authenticated or orgId is missing')

      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/folders/${folderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return response.json()
    },
    onSuccess: async () => {
      // キャッシュを完全にクリアして再取得
      queryClient.removeQueries({ queryKey: ['folders', user?.orgId] })
      await queryClient.invalidateQueries({ queryKey: ['folders', user?.orgId] })
      // 強制的に再取得
      await queryClient.refetchQueries({ queryKey: ['folders', user?.orgId] })
    }
  })
}
